import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import { priceFormatter } from "~/utils/price-formatter";
import { T, useT } from "@repo/transifex";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { Button, Dialog, DialogLoading, DialogPayment } from "~/components";
import { useOrder, useOrderCancel } from "~/hooks/queries/orders";
import { usePaymentCreate } from "~/hooks/queries/payments";
import { useDeviceData } from "~/providers/app";
import { useOpenCashDrawer } from "~/hooks";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { useSkipReceipt } from "~/hooks/use-skip-receipt";
import { useSplitPaymentFlow } from "~/hooks/use-split-payment-flow";
import { useUserBalance } from "~/hooks/use-user-balance";
import { calculateAmountForSaldo } from "~/utils/basket";
import { useBasketContext } from "~/providers/store/basket/context";

import { MobilePayIcon } from "../../components/MobilePayIcon";

const PosMobilePay = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const t = useT();

  const skipReceipt = useSkipReceipt();

  const navigate = useNavigate();

  const openCashDrawer = useOpenCashDrawer();

  const { locale } = useCurrentTillLocalisation();
  const { data: order, status } = useOrder(orderId);
  const { os, osVersion } = useDeviceData();
  const { mutate: payOrder, status: createPaymentStatus } = usePaymentCreate();
  const { mutate: cancelOrder, status: cancelOrderStatus } = useOrderCancel();
  const { isActive: isSplitPaymentActive, unitReceived } =
    useSplitPaymentFlow();

  const { data: userProducts } = useUserProductsPos();

  const unitPriceTotalInclVat = useBasketContext(
    (state) => state.sum.unitPriceTotalInclVat
  );

  const availableBalance = useUserBalance({
    unitAvailableInclVat:
      userProducts?.user?.collectLimit?.unitAvailableInclVat,
    balance: userProducts?.user?.balance
  });

  const actionsLoading =
    createPaymentStatus === "pending" || cancelOrderStatus === "pending";

  const amountToPay =
    calculateAmountForSaldo(unitPriceTotalInclVat, availableBalance)
      .payWithOther - unitReceived;

  if (status === "pending") {
    return <DialogLoading title={<T _str="Preparing payment" />} />;
  }

  if (
    status === "error" ||
    order.status === "failed" ||
    order.status === "cancelled"
  ) {
    return <DialogPayment status="error" />;
  }

  if (order.status === "paid") {
    return <DialogPayment orderId={order.id} status="success" />;
  }

  return (
    <Dialog className="max-w-sm" size="md">
      <div className="flex w-full flex-col items-center p-4">
        <p className="mb-4 mt-6 text-base">
          <T _str="Amount" />
        </p>

        <p className="mb-14 text-2xl font-bold">
          {priceFormatter({
            value: amountToPay,
            currency: order.currency,
            locale
          })}
        </p>

        <div className="mb-6 flex w-44 flex-row items-center justify-center gap-2 rounded-2xl bg-info-backdrop p-5">
          <MobilePayIcon />
          <p className="text-lg font-bold text-info-dark">
            <T _str="MobilePay" />
          </p>
        </div>

        <p className="mb-14 text-base">
          <T _str="Make sure the customer has paid" />
        </p>

        <div className="flex w-full justify-center gap-3">
          <Button
            className="flex-1"
            disabled={actionsLoading}
            onClick={() => {
              if (orderId) {
                cancelOrder(orderId);

                if (isSplitPaymentActive) {
                  openCashDrawer("automatic");
                }
                navigate("..");
              }
            }}
            variant="danger"
          >
            <T _str="Cancel" />
          </Button>

          <Button
            className="flex-[2]"
            disabled={actionsLoading}
            icon={createPaymentStatus === "pending" ? faSpinner : undefined}
            loading={actionsLoading}
            loadingText={t("Processing order")}
            onClick={() => {
              if (orderId && os && osVersion)
                payOrder(
                  {
                    orderId,
                    paymentProvider: "mobilepay_own",
                    deviceInfo: {
                      os,
                      osVersion
                    },
                    unitCashReceived: unitReceived
                  },
                  {
                    onSuccess: () => {
                      skipReceipt(orderId);
                    }
                  }
                );
            }}
          >
            <T _str="Complete payment" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export { PosMobilePay };
